import { useQuery } from "@apollo/client";
import { GET_MANAGED_SUPPORT_CONFIG } from "../graphql/operations/managedSupport";
import useSubscriptionBillingCurrencies from "./useSubscriptionBillingCurrencies";

export type ManagedSupportCurrency = "usd" | "inr";

const CURRENCY_META: Record<ManagedSupportCurrency, { symbol: string; label: string; locale: string }> = {
    usd: { symbol: "$", label: "USD", locale: "en-US" },
    inr: { symbol: "₹", label: "INR", locale: "en-IN" },
};

export function formatSeatPrice(minorUnits: number, currency: string = "usd"): string {
    const code: ManagedSupportCurrency = String(currency).toLowerCase() === "inr" ? "inr" : "usd";
    const meta = CURRENCY_META[code];
    return `${meta.symbol}${Math.round((minorUnits || 0) / 100).toLocaleString(meta.locale)}`;
}

/**
 * Managed-support seat pricing as quoted by the backend, which resolves the
 * currency from `SUBSCRIPTIONS_CURRENCY` / `DEFAULT_BILLING_CURRENCY`. Marketing
 * surfaces are public, so this queries the unscoped config (no organizationId).
 *
 * Labels are `null` until the query resolves — render a placeholder rather than
 * a hardcoded amount, otherwise an INR-only deployment flashes USD prices.
 */
export function useManagedSupportPricing() {
    const { data, loading } = useQuery(GET_MANAGED_SUPPORT_CONFIG, { fetchPolicy: "cache-first" });
    const { currencies, isSingleCurrency } = useSubscriptionBillingCurrencies();

    const config = data?.managedSupportConfig;
    const currency: ManagedSupportCurrency = String(config?.currency).toLowerCase() === "inr" ? "inr" : "usd";
    const ready = Boolean(config);
    const providerLabel = config?.provider === "razorpay" ? "Razorpay" : "Stripe";

    return {
        ready,
        loading,
        currency,
        currencyLabel: CURRENCY_META[currency].label,
        providerLabel,
        agentPrice: ready ? formatSeatPrice(config.agentMonthlyCents, currency) : null,
        supervisorPrice: ready ? formatSeatPrice(config.supervisorMonthlyCents, currency) : null,
        // "INR via Razorpay" on a single-currency deployment, both rails otherwise.
        billingSummary: isSingleCurrency
            ? `${CURRENCY_META[currencies[0] === "inr" ? "inr" : "usd"].label} via ${currencies[0] === "inr" ? "Razorpay" : "Stripe"}`
            : "USD via Stripe or INR via Razorpay",
    };
}

export default useManagedSupportPricing;
